import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AdminLayout from '@/layouts/AdminLayout';
import { useAuthState } from '@/hooks/useAuthState';
import { DevelopmentService, Development } from '@/services/developmentService';
import { getDevelopmentLogoUrl } from '@/config/api';
import { 
  Building2, 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  ArrowLeft,
  Globe,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  HelpCircle,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from '@/hooks/use-toast';

const AdminDevelopments = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, userType, userName, userPhoto, userId, loading: authLoading } = useAuthState();
  const [developments, setDevelopments] = useState<Development[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Verificar se é admin (AdminLayout já faz isso)

  useEffect(() => {
    loadDevelopments();
  }, [currentPage, itemsPerPage, searchTerm, filterStatus]);

  const loadDevelopments = async () => {
    try {
      setLoading(true);
      
      const allDevelopments = await DevelopmentService.getAllDevelopmentsForAdmin();

      // Aplicar filtros
      let filteredDevelopments = allDevelopments;
      
      // Filtro por termo de busca
      if (searchTerm) {
        filteredDevelopments = filteredDevelopments.filter(development => 
          development.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (development.email && development.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (development.city && development.city.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }

      // Filtro por status
      if (filterStatus !== 'todos') {
        filteredDevelopments = filteredDevelopments.filter(development => {
          if (filterStatus === 'ativo') {
            return Boolean(development.is_active) === true;
          } else if (filterStatus === 'inativo') {
            return Boolean(development.is_active) === false;
          }
          return true;
        });
      }

      // Calcular paginação
      const total = filteredDevelopments.length;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedDevelopments = filteredDevelopments.slice(startIndex, endIndex);

      setDevelopments(paginatedDevelopments);
      setTotalItems(total);
    } catch (error) {
      console.error('Erro ao carregar empreendimentos:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar empreendimentos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleEditDevelopment = (developmentId: number) => {
    navigate(`/admin/empreendimentos/editar/${developmentId}`);
  };

  const handleToggleDevelopmentStatus = async (developmentId: number) => {
    const development = developments.find(d => d.id === developmentId);
    if (!development) return;
    
    const action = development.is_active ? 'desativar' : 'ativar';
    if (confirm(`Tem certeza que deseja ${action} este empreendimento?`)) {
      try {
        await DevelopmentService.toggleDevelopmentStatus(developmentId);
        toast({
          title: 'Sucesso',
          description: `Empreendimento ${action === 'ativar' ? 'ativado' : 'desativado'} com sucesso`,
        });
        loadDevelopments();
      } catch (error) {
        console.error(`Erro ao ${action} empreendimento:`, error);
        toast({
          title: 'Erro',
          description: `Erro ao ${action} empreendimento`,
          variant: 'destructive',
        });
      }
    }
  };

  const handleDeleteDevelopment = async (developmentId: number) => {
    const development = developments.find(d => d.id === developmentId);
    if (!development) return;
    
    if (confirm(`Tem certeza que deseja excluir o empreendimento "${development.name}"? Esta ação não pode ser desfeita.`)) {
      try {
        await DevelopmentService.deleteDevelopment(developmentId);
        toast({
          title: 'Sucesso',
          description: 'Empreendimento excluído com sucesso',
        });
        loadDevelopments();
      } catch (error: any) {
        console.error('Erro ao excluir empreendimento:', error);
        toast({
          title: 'Erro',
          description: error.message || 'Erro ao excluir empreendimento',
          variant: 'destructive',
        });
      }
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando empreendimentos...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Gerenciar Empreendimentos</h1>
                <p className="text-muted-foreground">
                  Cadastre, edite e gerencie empreendimentos do sistema
                </p>
              </div>
            </div>
            <Button onClick={() => navigate('/admin/empreendimentos/novo')}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Empreendimento
            </Button>
          </div>

          {/* Busca */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        placeholder="Buscar por nome, email ou cidade..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="pl-10"
                      />
                    </div>
                    <Button 
                      onClick={handleSearch}
                      variant="outline"
                      className="px-4"
                    >
                      <Search className="h-4 w-4" />
                    </Button>
                    {searchTerm && (
                      <Button 
                        onClick={handleClearSearch}
                        variant="outline"
                        className="px-4"
                      >
                        Limpar
                      </Button>
                    )}
                  </div>
                  {searchTerm && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Buscando por: <strong>"{searchTerm}"</strong>
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  <Select value={filterStatus} onValueChange={(value) => {
                    setFilterStatus(value);
                    setCurrentPage(1);
                  }}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="ativo">Ativo</SelectItem>
                      <SelectItem value="inativo">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Itens por página:</span>
                  <Select value={itemsPerPage.toString()} onValueChange={(value) => handleItemsPerPageChange(parseInt(value))}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabela de Empreendimentos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Empreendimentos ({totalItems} total)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {developments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum empreendimento encontrado
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Empreendimento</TableHead>
                      <TableHead>Cidade/Estado</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Telefone</TableHead>
                      <TableHead>Website</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Data de Cadastro</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {developments.map((development) => {
                      const DevelopmentLogo = ({ logo, name }: { logo: string | null | undefined, name: string }) => {
                        const [imageError, setImageError] = useState(false);
                        
                        if (!logo || imageError) {
                          return (
                            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Building2 className="h-6 w-6 text-primary" />
                            </div>
                          );
                        }
                        
                        return (
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <img
                              src={getDevelopmentLogoUrl(logo)}
                              alt={`Logo ${name}`}
                              className="w-full h-full object-contain"
                              onError={() => setImageError(true)}
                            />
                          </div>
                        );
                      };
                      
                      return (
                        <TableRow key={development.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-3">
                              <DevelopmentLogo logo={development.logo} name={development.name} />
                              <div>
                                <div className="font-medium">{development.name}</div>
                                {development.description && (
                                  <div className="text-sm text-muted-foreground line-clamp-1">
                                    {development.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        <TableCell>
                          {development.city && development.state 
                            ? `${development.city}/${development.state}`
                            : '-'}
                        </TableCell>
                        <TableCell>{development.email || '-'}</TableCell>
                        <TableCell>{development.phone || '-'}</TableCell>
                        <TableCell>
                          {development.website ? (
                            <div className="flex items-center gap-2 text-sm text-blue-600">
                              <Globe className="h-4 w-4" />
                              <a 
                                href={development.website} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="hover:underline"
                              >
                                Visitar
                              </a>
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={development.is_active ? 'default' : 'secondary'}>
                            {development.is_active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(development.created_at).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditDevelopment(development.id)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate(`/admin/empreendimentos/${development.id}/faq`)
                                }
                              >
                                <HelpCircle className="h-4 w-4 mr-2" />
                                FAQ
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleToggleDevelopmentStatus(development.id)}
                                className={development.is_active ? "text-red-600" : "text-green-600"}
                              >
                                {development.is_active ? (
                                  <>
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Desativar
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Ativar
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDeleteDevelopment(development.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Paginação */}
          {totalPages > 1 && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} registros
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Anterior
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            className="w-8 h-8 p-0"
                          >
                            {page}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Próxima
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDevelopments;

